"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { FiArrowUp, FiArrowDown, FiCopy } from "react-icons/fi";
import { useVisibilityRef } from "@/hooks/useVisibilityRef";
import { ChevronDownIcon } from "@/app/components/icons/ChevronDownIcon";
import { useT } from "@/hooks/useT";
import { InfoIcon } from "@/app/components/icons/InfoIcon";
import WhaleIcon from "@/app/components/icons/WhaleIcon";
import Tooltip from "@/app/components/Tooltip";
import dynamic from "next/dynamic";
const TradingModal = dynamic(() => import("@/app/components/TradingModal"), { ssr: false });
import GradientAvatar from "@/app/components/GradientAvatar";

// Trade-size tiers for the whale tag. A single trade above these thresholds
// is a strong signal of serious money moving on the market.
const WHALE_THRESHOLD = 10_000;  // $10K+ → 🐋 whale
const BIG_TRADE_THRESHOLD = 1_000; // $1K+ → highlighted

interface Trade {
  id: string;
  timestamp: number;
  price: number;
  size: number;
  side: string;
  outcome: string;
  outcomeIndex: number;
  user: {
    name: string | null;
    pseudonym: string | null;
    profileImage: string | null;
    /** Wallet / proxy address — used to deep-link the row to the trader
     *  profile (/traders/<address>). Null for anonymous trades. */
    wallet: string | null;
  };
}

interface RecentTradesProps {
  marketSlug: string;
  clobTokenIds: string[];
  conditionId: string | null;
  marketQuestion?: string;
  marketImage?: string | null;
  outcomes?: string[];
  isActive?: boolean;
  alwaysExpanded?: boolean;
  /** Hide the title/toggle header when rendered inside a tab that already labels it. */
  hideHeader?: boolean;
  source?: "polymarket" | "kalshi" | "predict" | "hyperliquid";
  predictMarketId?: string;
  /** Pre-fetched trades from parent — skips internal fetching when provided */
  externalTrades?: Trade[] | null;
}

export default function RecentTrades({
  marketSlug,
  clobTokenIds,
  conditionId,
  marketQuestion = "Unknown Market",
  marketImage,
  outcomes = ["Yes", "No"],
  isActive = true,
  alwaysExpanded,
  hideHeader,
  source = "polymarket",
  predictMarketId,
  externalTrades,
}: RecentTradesProps) {
  const t = useT();
  const [trades, setTrades] = useState<Trade[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [isExpanded, setIsExpanded] = useState(alwaysExpanded || false);
  const visRef = useVisibilityRef();
  const listRef = useRef<HTMLDivElement>(null);
  const prevTradeCountRef = useRef(0);
  const [newTradeIds, setNewTradeIds] = useState<Set<string>>(new Set());

  // Click-and-drag horizontal scroll for the filter chips row (mirrors the
  // OutcomeDetailPanel tabs pattern). Window-level listeners so drag keeps
  // tracking even if the cursor leaves a chip child.
  const filtersRef = useRef<HTMLDivElement>(null);
  const lastDragMovedRef = useRef(false);
  const onFiltersMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!filtersRef.current || e.button !== 0) return;
    const startX = e.clientX;
    const startScroll = filtersRef.current.scrollLeft;
    lastDragMovedRef.current = false;
    const handleMove = (ev: MouseEvent) => {
      if (!filtersRef.current) return;
      const dx = ev.clientX - startX;
      if (Math.abs(dx) > 4) lastDragMovedRef.current = true;
      filtersRef.current.scrollLeft = startScroll - dx;
    };
    const handleUp = () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      setTimeout(() => { lastDragMovedRef.current = false; }, 0);
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
  };

  // Trading modal state
  const [tradingModal, setTradingModal] = useState<{
    isOpen: boolean;
    outcome: "YES" | "NO";
    actualOutcome: string;
    price: number;
  }>({ isOpen: false, outcome: "YES", actualOutcome: "Yes", price: 0.5 });

  // Filters
  const [outcomeFilter, setOutcomeFilter] = useState<"all" | "yes" | "no">("all");
  const [minAmount, setMinAmount] = useState<number>(0);
  const [sortBy, setSortBy] = useState<"time" | "amount">("time");
  const [whalesOnly, setWhalesOnly] = useState<boolean>(false);
  const [headerHovered, setHeaderHovered] = useState(false);

  // When external trades are provided, sync to local state (no internal fetching)
  useEffect(() => {
    if (!externalTrades) return;
    setTrades(externalTrades);
    setLoading(false);
    setInitialLoad(false);
  }, [externalTrades]);

  useEffect(() => {
    // Skip internal fetching when parent provides data
    if (externalTrades) return;

    async function fetchTrades() {
      if (source === "predict") {
        if (!predictMarketId) { setLoading(false); setInitialLoad(false); return; }
      } else if (!conditionId) {
        setLoading(false);
        setInitialLoad(false);
        return;
      }

      try {
        if (initialLoad) setLoading(true);

        let tradeData: Trade[] = [];

        if (source === "predict" && predictMarketId) {
          // Predict.fun trades API
          const response = await fetch(`/api/markets/predict/${predictMarketId}/trades?limit=20`);
          if (response.ok) {
            const result = await response.json();
            tradeData = (result.trades || []).map((t: any) => ({
              id: t.id || `${t.timestamp}-${t.price}`,
              timestamp: t.timestamp || t.createdAt || new Date().toISOString(),
              price: t.price || 0,
              size: t.size || t.amount || 0,
              side: t.side || "BUY",
              outcome: t.outcome || "Yes",
              outcomeIndex: t.outcomeIndex ?? (t.outcome?.toLowerCase() === "no" ? 1 : 0),
              user: {
                name: t.user?.name || null,
                pseudonym: t.user?.pseudonym || null,
                profileImage: t.user?.profileImage || null,
                wallet: t.user?.address || t.user?.wallet || t.proxyWallet || null,
              },
            }));
          }
        } else {
          // Polymarket trades API — returns raw array of CLOB trades
          const response = await fetch(`/api/polymarket/trades?market=${conditionId}&limit=20`);
          if (!response.ok) throw new Error("Failed to fetch trades");
          const result = await response.json();
          // API returns array directly OR { data: [...] }
          const rawTrades = Array.isArray(result) ? result : (result.data || result.trades || []);
          tradeData = rawTrades.map((t: any) => {
            // Map outcome from asset (tokenId): asset === tokenIds[0] = Yes, else No
            const tokenId = String(t.asset || t.tokenId || '');
            const tokenIdx = clobTokenIds?.findIndex(id => String(id) === tokenId) ?? -1;
            const outcomeIdx = tokenIdx >= 0 ? tokenIdx : (t.outcomeIndex ?? 0);
            const outcomeLabel = outcomes?.[outcomeIdx] || (outcomeIdx === 0 ? "Yes" : "No");
            const ts = typeof t.timestamp === 'number' ? t.timestamp : Number(t.timestamp);
            return {
              id: t.id || t.transactionHash || `${tokenId}-${ts}-${t.price}`,
              timestamp: ts > 1e12 ? ts : ts * 1000, // normalize to ms
              price: Number(t.price) || 0,
              size: Number(t.size) || 0,
              side: t.side || "BUY",
              outcome: outcomeLabel,
              outcomeIndex: outcomeIdx,
              user: {
                name: t.user?.name || t.name || null,
                pseudonym: t.user?.pseudonym || t.pseudonym || null,
                // Only a REAL profile image — `t.icon` in the PM trades
                // payload is the market's icon, not the user's, so it used
                // to render every anonymous trade with the market cover
                // as an avatar. GradientAvatar handles the null case.
                profileImage: t.user?.profileImage || t.profileImage || null,
                wallet: t.user?.proxyWallet || t.proxyWallet || t.user?.address || t.maker || null,
              },
            };
          });
        }

        if (tradeData.length > 0) {
          // Detect new trades for animation
          if (prevTradeCountRef.current > 0) {
            const existingIds = new Set(trades.map((t: Trade) => t.id));
            const newIds = new Set<string>(tradeData.filter((t: Trade) => !existingIds.has(t.id)).map((t: Trade) => t.id));
            if (newIds.size > 0) {
              setNewTradeIds(newIds);
              setTimeout(() => setNewTradeIds(new Set<string>()), 300);
            }
          }
          prevTradeCountRef.current = tradeData.length;
          setTrades(tradeData);
        }
      } catch (err) {
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    }

    fetchTrades();

    // Poll only while visible and expanded to reduce background fanout.
    if (!isExpanded && !alwaysExpanded) return;
    const interval = setInterval(() => { if (visRef.current) fetchTrades(); }, 15000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conditionId, isExpanded, alwaysExpanded]);

  const formatTimeAgo = (timestamp: number) => {
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  // Apply filters and sorting
  const filteredTrades = trades
    .filter(trade => {
      // Outcome filter (YES/NO or Up/Down)
      if (outcomeFilter === "yes" && trade.outcomeIndex !== 0) return false;
      if (outcomeFilter === "no" && trade.outcomeIndex !== 1) return false;

      // Minimum amount filter (price * size)
      const tradeAmount = trade.price * trade.size;
      if (tradeAmount < minAmount) return false;
      if (whalesOnly && tradeAmount < WHALE_THRESHOLD) return false;

      return true;
    })
    .sort((a, b) => {
      if (sortBy === "time") {
        return b.timestamp - a.timestamp; // Most recent first
      } else {
        // Sort by amount (price * size)
        const amountA = a.price * a.size;
        const amountB = b.price * b.size;
        return amountB - amountA; // Largest first
      }
    });

  return (
    <div className={hideHeader ? "" : "bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors overflow-hidden"}>
      {/* Header */}
      {!hideHeader && (
        <button
          onClick={() => !alwaysExpanded && setIsExpanded(!isExpanded)}
          onMouseEnter={() => setHeaderHovered(true)}
          onMouseLeave={() => setHeaderHovered(false)}
          className={`w-full flex items-center justify-between p-4 transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gray-400/50 focus-visible:outline-none ${
            alwaysExpanded ? 'cursor-default' : 'hover:bg-gray-50 dark:hover:bg-[#0f0f12]/50'
          }`}
        >
          <div className="flex items-center gap-2">
            <InfoIcon size={20} className="text-gray-400 dark:text-gray-500" isHovered={headerHovered} />
            <h3 className="font-monument text-lg text-gray-900 dark:text-white">
              Recent Trades
            </h3>
            {trades.length > 0 && (
              <span className="px-2 py-0.5 bg-gray-100 dark:bg-[#0f0f12] text-gray-500 dark:text-gray-400 text-xs font-inter-tight font-medium rounded-[5px]">
                {trades.length}
              </span>
            )}
          </div>
          {!alwaysExpanded && (
            <div className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
              <ChevronDownIcon size={20} className="text-gray-400" isHovered={headerHovered} />
            </div>
          )}
        </button>
      )}

      {/* Collapsible Content */}
      <div className={`transition-[max-height,opacity] duration-200 ease-in-out ${(isExpanded || alwaysExpanded) ? 'max-h-[1200px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
        <div className="px-4 pb-4">
          {/* Loading State */}
          {loading && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 px-1">
                <div className="w-4 h-4 border-2 border-gray-300 dark:border-gray-600 border-t-gray-500 dark:border-t-gray-400 rounded-full animate-spin" />
                <span className="text-xs text-gray-500 dark:text-gray-500">{t("common.loadingTrades")}</span>
              </div>
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-12 skeleton-shimmer rounded-[5px]" style={{ animationDelay: `${i * 100}ms` }} />
              ))}
            </div>
          )}

          {/* Content when loaded */}
          {!loading && (
            <>
              {/* Filters — compact chips, single row with horizontal scroll
                  + click-and-drag on narrow screens. */}
              <div
                ref={filtersRef}
                onMouseDown={onFiltersMouseDown}
                className="mb-4 flex items-center gap-2 flex-nowrap overflow-x-auto scrollbar-hide select-none touch-pan-x cursor-grab active:cursor-grabbing"
              >
                {/* Outcome chips */}
                {(["all", "yes", "no"] as const).map((filter) => (
                  <button
                    key={filter}
                    onClick={(e) => {
                      if (lastDragMovedRef.current) { e.preventDefault(); lastDragMovedRef.current = false; return; }
                      setOutcomeFilter(filter);
                    }}
                    className={`flex-shrink-0 font-inter-tight px-3 py-1.5 rounded-[5px] text-xs font-semibold transition-colors focus-visible:ring-2 focus-visible:ring-gray-400/50 focus-visible:outline-none ${
                      outcomeFilter === filter
                        ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                        : "bg-gray-100 dark:bg-[#0f0f12] text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                    }`}
                  >
                    {filter === "all" ? t("recentTrades.filter.all") : filter.toUpperCase()}
                  </button>
                ))}

                <span className="flex-shrink-0 w-px h-4 bg-gray-200 dark:bg-[#1A1A1A]" />

                {/* Sort chips */}
                {(["time", "amount"] as const).map((sort) => (
                  <button
                    key={sort}
                    onClick={(e) => {
                      if (lastDragMovedRef.current) { e.preventDefault(); lastDragMovedRef.current = false; return; }
                      setSortBy(sort);
                    }}
                    className={`flex-shrink-0 font-inter-tight px-3 py-1.5 rounded-[5px] text-xs font-semibold transition-colors focus-visible:ring-2 focus-visible:ring-gray-400/50 focus-visible:outline-none ${
                      sortBy === sort
                        ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                        : "bg-gray-100 dark:bg-[#0f0f12] text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                    }`}
                  >
                    {sort === "time" ? t("recentTrades.sort.latest") : t("recentTrades.sort.biggest")}
                  </button>
                ))}

                <span className="flex-shrink-0 w-px h-4 bg-gray-200 dark:bg-[#1A1A1A]" />

                {/* Whales-only toggle */}
                <button
                  onClick={(e) => {
                    if (lastDragMovedRef.current) { e.preventDefault(); lastDragMovedRef.current = false; return; }
                    setWhalesOnly((w) => !w);
                  }}
                  className={`flex-shrink-0 font-inter-tight px-3 py-1.5 rounded-[5px] text-xs font-semibold inline-flex items-center gap-1.5 transition-colors focus-visible:ring-2 focus-visible:ring-gray-400/50 focus-visible:outline-none ${
                    whalesOnly
                      ? "bg-amber-500/20 border border-amber-500/50 text-amber-600 dark:text-amber-400 shadow-sm"
                      : "bg-amber-500/5 border border-amber-500/20 text-amber-600 dark:text-amber-400 hover:bg-amber-500/10 hover:border-amber-500/30"
                  }`}
                  title={t("recentTrades.whalesTooltip")}
                >
                  <WhaleIcon size={14} className="text-amber-500" />
                  {t("recentTrades.whales")}
                </button>
              </div>

              <div ref={listRef} className="space-y-0.5 max-h-[600px] overflow-y-auto">
                {filteredTrades.length === 0 ? (
                  <p className="font-satoshi text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                    {t("recentTrades.empty")}
                  </p>
                ) : (
                  filteredTrades.map((trade) => {
                    const isYes = trade.outcomeIndex === 0;
                    const userName = trade.user.name || trade.user.pseudonym || t("recentTrades.anonymous");
                    const tradeAmount = trade.price * trade.size;
                    const actualOutcome = outcomes[trade.outcomeIndex] || (isYes ? "Yes" : "No");
                    const avatarSeed = trade.user.pseudonym || trade.user.name || trade.id;
                    // Truncate wallet-address-style usernames for visual rhythm
                    const shortName = userName.length > 18 && /^0x[a-fA-F0-9]/.test(userName)
                      ? `${userName.slice(0, 6)}…${userName.slice(-4)}`
                      : userName;
                    const sideColor = isYes ? "#00FF9D" : "#FF1744";

                    const profileHref = trade.user.wallet ? `/traders/${trade.user.wallet}` : null;
                    const AvatarWrapper: React.ElementType = profileHref ? Link : "div";
                    const avatarProps = profileHref ? { href: profileHref } : {};
                    return (
                      <div
                        key={trade.id}
                        className={`group flex items-center gap-3 py-2 px-2.5 rounded-[5px] hover:bg-gray-50 dark:hover:bg-white/[0.03] transition-colors ${newTradeIds.has(trade.id) ? 'animate-slide-in-left' : ''}`}
                      >
                        {/* Avatar + corner side badge. Wrapped in a Link to the
                            trader profile when we know the wallet. The badge
                            itself stays — clicking it also takes you to the
                            profile (natural, since the avatar IS the user). */}
                        <AvatarWrapper
                          {...avatarProps}
                          className={`relative flex-shrink-0 ${profileHref ? "cursor-pointer hover:opacity-80 transition-opacity" : ""}`}
                        >
                          {trade.user.profileImage ? (
                            <img
                              src={trade.user.profileImage}
                              alt={userName}
                              className="w-8 h-8 rounded-[5px] object-cover"
                            />
                          ) : (
                            <GradientAvatar seed={avatarSeed} size={32} className="!rounded-[5px]" />
                          )}
                          <span
                            className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-[5px] border-2 border-white dark:border-[#18181D] flex items-center justify-center"
                            style={{ backgroundColor: sideColor }}
                            aria-label={isYes ? "Buy Yes" : "Buy No"}
                          >
                            {isYes ? (
                              <FiArrowUp className="w-2 h-2 text-black" strokeWidth={3.5} />
                            ) : (
                              <FiArrowDown className="w-2 h-2 text-white" strokeWidth={3.5} />
                            )}
                          </span>
                        </AvatarWrapper>

                        {/* Name + time. Name is a separate Link so hovering the
                            row doesn't look like one giant button — only the
                            avatar and name are interactive. Whale tag stays
                            inline when the trade is ≥ $10K. */}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 min-w-0">
                            {profileHref ? (
                              <Link
                                href={profileHref}
                                className="font-inter-tight text-[13px] font-semibold text-gray-900 dark:text-white truncate hover:underline underline-offset-2"
                              >
                                {shortName}
                              </Link>
                            ) : (
                              <span className="font-inter-tight text-[13px] font-semibold text-gray-900 dark:text-white truncate">
                                {shortName}
                              </span>
                            )}
                            {tradeAmount >= WHALE_THRESHOLD && (
                              <Tooltip content={`Whale — $${tradeAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}>
                                <WhaleIcon size={12} className="text-amber-500 flex-shrink-0" />
                              </Tooltip>
                            )}
                          </div>
                          <div className="font-satoshi text-[11px] text-gray-500 dark:text-gray-400">
                            {formatTimeAgo(trade.timestamp)}
                          </div>
                        </div>

                        {/* Price + volume summary, one line. `sideColor` on the
                            cent price, neutral gray on shares/amount so the
                            important number (price) reads first. */}
                        <div className="text-right flex-shrink-0">
                          <div className="font-inter-tight text-[13px] font-semibold tabular-nums" style={{ color: sideColor }}>
                            {(trade.price * 100).toFixed(1)}¢
                          </div>
                          <div className="font-satoshi text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
                            {trade.size.toLocaleString(undefined, { maximumFractionDigits: 0 })} × ${tradeAmount.toFixed(2)}
                          </div>
                        </div>

                        {/* Copy trade — only renders on hover, in place of a
                            always-visible button that pushed the row wider. */}
                        {isActive && (
                          <button
                            onClick={() => setTradingModal({
                              isOpen: true,
                              outcome: isYes ? "YES" : "NO",
                              actualOutcome,
                              price: trade.price,
                            })}
                            className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 bg-gray-900 dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-100 rounded-[5px]"
                            title={`Copy this ${actualOutcome} trade`}
                          >
                            <FiCopy className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Trading Modal */}
      <TradingModal
        isOpen={tradingModal.isOpen}
        onClose={() => setTradingModal({ ...tradingModal, isOpen: false })}
        marketSlug={marketSlug}
        outcome={tradingModal.outcome}
        actualOutcome={tradingModal.actualOutcome}
        outcomePrice={tradingModal.price}
        marketQuestion={marketQuestion}
        marketImage={marketImage}
      />
    </div>
  );
}
